import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
  Stack,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import * as d3 from 'd3';
import axios from '../config/axiosConfig';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// ─── Filter Options ───
const SIZE_OPTIONS = [
  { value: 'marketCap', label: 'Market cap' },
  { value: 'volume', label: 'Volume' },
  { value: 'mono', label: 'Mono size' }
];

const COLOR_OPTIONS = [
  { value: '1D', label: 'Change D, %' },
  { value: '5D', label: 'Performance 5D, %' },
  { value: '1M', label: 'Performance M, %' },
  { value: '3M', label: 'Performance 3M, %' },
  { value: '6M', label: 'Performance 6M, %' },
  { value: 'YTD', label: 'Performance YTD, %' },
  { value: '1Y', label: 'Performance Y, %' },
  { value: '3Y', label: 'Performance 3Y, %' },
  { value: '5Y', label: 'Performance 5Y, %' }
];

const GROUP_OPTIONS = [
  { value: 'sector', label: 'Sector' },
  { value: 'sector-industry', label: 'Sector / Industry' },
  { value: 'none', label: 'No group' }
];

const DOWNLOAD_FORMATS = ["PNG", "JPG", "SVG"];

const COLOR_RANGES = {
  '1D': 3, '5D': 5, '1M': 10, '3M': 20, '6M': 30,
  'YTD': 40, '1Y': 50, '3Y': 100, '5Y': 100
};

// ─── Color Function ───
function computeColor(changePercent, colorBy) {
  const maxRange = COLOR_RANGES[colorBy] || 3;
  const clamped = Math.max(-maxRange, Math.min(maxRange, changePercent));
  const norm = clamped / maxRange;

  if (norm > 0) {
    const i = Math.abs(norm);
    return `rgb(${Math.round(30 - i * 10)}, ${Math.round(60 + i * 120)}, ${Math.round(40 - i * 20)})`;
  } else if (norm < 0) {
    const i = Math.abs(norm);
    return `rgb(${Math.round(80 + i * 140)}, ${Math.round(30 - i * 20)}, ${Math.round(35 - i * 20)})`;
  }
  return 'rgb(50, 50, 55)';
}

// ─── Build d3 hierarchy ───
function buildHierarchy(data, groupBy, sizeBy, colorBy) {
  const getSize = (item) => {
    if (sizeBy === 'volume') return item.volume || 1;
    if (sizeBy === 'mono') return 1;
    return item.marketCap || 1;
  };

  const getChange = (item) => item.changes?.[colorBy] || 0;

  const makeLeaf = (stock) => ({
    name: stock.symbol,
    ticker: stock.symbol,
    companyName: stock.name,
    size: getSize(stock),
    changePercent: getChange(stock),
    price: stock.price,
    volume: stock.volume,
    marketCap: stock.marketCap,
    sector: stock.sector,
    industry: stock.industry,
    color: computeColor(getChange(stock), colorBy)
  });

  if (groupBy === 'none') {
    return { name: 'root', children: data.map(makeLeaf) };
  }

  if (groupBy === 'sector') {
    const groups = {};
    data.forEach(item => {
      const sec = item.sector || 'Other';
      if (!groups[sec]) groups[sec] = [];
      groups[sec].push(makeLeaf(item));
    });
    return {
      name: 'root',
      children: Object.entries(groups).map(([name, children]) => ({ name, children }))
    };
  }

  // sector-industry
  const groups = {};
  data.forEach(item => {
    const sec = item.sector || 'Other';
    const ind = item.industry || 'Other';
    if (!groups[sec]) groups[sec] = {};
    if (!groups[sec][ind]) groups[sec][ind] = [];
    groups[sec][ind].push(makeLeaf(item));
  });
  return {
    name: 'root',
    children: Object.entries(groups).map(([secName, industries]) => ({
      name: secName,
      children: Object.entries(industries).map(([indName, stocks]) => ({
        name: indName,
        children: stocks
      }))
    }))
  };
}

// ─── Tooltip HTML ───
function buildTooltipHTML(d, isGroup) {
  if (isGroup) {
    const collectLeaves = (node) => {
      if (node.children) return node.children.flatMap(collectLeaves);
      return [node];
    };
    const leaves = collectLeaves(d.data);
    const count = leaves.length;
    const avg = leaves.reduce((s, l) => s + (l.changePercent || 0), 0) / count;
    const avgColor = avg >= 0 ? '#26a69a' : '#ef5350';
    return `
      <div style="font-weight:600;font-size:14px;margin-bottom:6px;">${d.data.name}</div>
      <div style="font-size:12px;color:#999;">Stocks: ${count}</div>
      <div style="font-size:12px;">Avg Change: <span style="color:${avgColor};font-weight:600;">${avg >= 0 ? '+' : ''}${avg.toFixed(2)}%</span></div>
    `;
  }

  const data = d.data;
  const cc = data.changePercent >= 0 ? '#26a69a' : '#ef5350';
  const fv = (v) => {
    if (!v) return 'N/A';
    if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
    if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    return `$${v.toLocaleString()}`;
  };

  return `
    <div style="font-weight:700;font-size:16px;">${data.ticker}</div>
    <div style="font-size:12px;color:#888;margin-bottom:8px;">${data.companyName || ''}</div>
    <div style="font-size:18px;margin-bottom:8px;">
      <span style="color:${cc};font-weight:700;">${data.changePercent >= 0 ? '+' : ''}${data.changePercent?.toFixed(2)}%</span>
    </div>
    <div style="font-size:12px;color:#aaa;margin-bottom:2px;">Price: <span style="color:#fff;">$${data.price?.toFixed(2) || 'N/A'}</span></div>
    <div style="font-size:12px;color:#aaa;margin-bottom:2px;">Mkt Cap: <span style="color:#fff;">${fv(data.marketCap)}</span></div>
    <div style="font-size:12px;color:#aaa;margin-bottom:6px;">Volume: <span style="color:#fff;">${(data.volume || 0).toLocaleString()}</span></div>
    ${data.sector ? `<div style="font-size:11px;color:#666;border-top:1px solid #333;padding-top:6px;">${data.sector}${data.industry ? ` › ${data.industry}` : ''}</div>` : ''}
  `;
}

// ═══════════════════════════════════════════════
// ─── Component ───
// ═══════════════════════════════════════════════
const INDEX_OPTIONS = [
  { value: 'SP500', label: 'S&P 500' },
  { value: 'NASDAQ', label: 'NASDAQ' },
  { value: 'DOWJONES', label: 'Dow Jones' }
];

export default function Heatmap({ index: defaultIndex = "SP500" }) {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const [sizeBy, setSizeBy] = useState('marketCap');
  const [colorBy, setColorBy] = useState('1D');
  const [groupBy, setGroupBy] = useState('sector');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const [anchorSize, setAnchorSize] = useState(null);
  const [anchorColor, setAnchorColor] = useState(null);
  const [anchorGroup, setAnchorGroup] = useState(null);
  const [anchorDownload, setAnchorDownload] = useState(null);
  const [anchorIndex, setAnchorIndex] = useState(null);

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const zoomRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ─── Fetch Data ───
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    axios.get(`/api/heatmap/${selectedIndex}`)
      .then(res => {
        if (res.data.success && res.data.data) {
          setData(res.data.data.data || []);
        } else {
          setError('No data received from server');
          setData([]);
        }
      })
      .catch(err => {
        console.error("Error fetching heatmap data:", err);
        setError(err.response?.data?.error || 'Failed to fetch heatmap data');
        setData([]);
      })
      .finally(() => setIsLoading(false));
  }, [selectedIndex]);

  // ─── Render Treemap ───
  const renderTreemap = useCallback(() => {
    const svgEl = svgRef.current;
    const tooltipEl = tooltipRef.current;
    if (!svgEl || !data.length) return;

    const width = svgEl.clientWidth;
    const height = svgEl.clientHeight;
    if (width === 0 || height === 0) return;

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    // ── Build hierarchy ──
    const hierarchyData = buildHierarchy(data, groupBy, sizeBy, colorBy);
    const root = d3.hierarchy(hierarchyData)
      .sum(d => d.size || 0)
      .sort((a, b) => b.value - a.value);

    // ── Treemap layout ──
    const treemap = d3.treemap()
      .size([width, height])
      .paddingTop(node => {
        if (groupBy === 'none') return 1;
        if (groupBy === 'sector') return node.depth === 1 ? 26 : 1;
        if (node.depth === 1) return 28;
        if (node.depth === 2) {
          const groupH = node.y1 - node.y0;
          return groupH < 50 ? 0 : 22;
        }
        return 1;
      })
      .paddingRight(1)
      .paddingBottom(1)
      .paddingLeft(1)
      .paddingInner(node => {
        if (node.depth === 0) return groupBy === 'sector-industry' ? 3 : 2;
        return 1;
      })
      .round(true)
      .tile(d3.treemapSquarify.ratio(1.2));

    treemap(root);

    // ── Defs ──
    const defs = svg.append('defs');

    // Text shadow filter
    const filter = defs.append('filter')
      .attr('id', 'text-shadow')
      .attr('x', '-20%').attr('y', '-20%')
      .attr('width', '140%').attr('height', '140%');
    filter.append('feDropShadow')
      .attr('dx', 0).attr('dy', 0)
      .attr('stdDeviation', 2)
      .attr('flood-color', 'rgba(0,0,0,0.8)');

    // Hover effect
    defs.append('style').text(`
      .cell rect { transition: filter 0.15s ease; }
      .cell:hover rect { filter: brightness(1.25); }
    `);

    // ── Zoom container ──
    const g = svg.append('g');

    const leaves = root.leaves();

    const zoom = d3.zoom()
      .scaleExtent([1, 10])
      .translateExtent([[0, 0], [width, height]])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        updateLabelVisibility(event.transform.k);
      });

    svg.call(zoom);
    svg.on('dblclick.zoom', null);

    zoomRef.current = { zoom, svg, width, height };

    // ── Mobile touch: allow d3 zoom to work ──
    const svgNode = svg.node();
    let touchCount = 0;
    let touchMoved = false;

    svgNode.addEventListener('touchstart', (e) => {
      e.preventDefault();
      touchCount = e.touches.length;
      touchMoved = false;
      if (touchCount > 1) hideTooltip();
    }, { passive: false });

    svgNode.addEventListener('touchmove', (e) => {
      e.preventDefault();
      touchMoved = true;
    }, { passive: false });

    // ── Tooltip with touch support ──
    let lastEventWasTouch = false;
    let tooltipsEnabled = false;

    setTimeout(() => { tooltipsEnabled = true; }, 500);

    svg.on('touchstart.inputtrack', () => { lastEventWasTouch = true; }, { passive: true });
    svg.on('mousemove.inputtrack', () => { lastEventWasTouch = false; });

    const positionTooltip = (clientX, clientY) => {
      if (!tooltipEl) return;
      tooltipEl.style.left = (clientX + 15) + 'px';
      tooltipEl.style.top = (clientY - 10) + 'px';
      requestAnimationFrame(() => {
        const rect = tooltipEl.getBoundingClientRect();
        if (rect.right > window.innerWidth - 10)
          tooltipEl.style.left = (clientX - rect.width - 15) + 'px';
        if (rect.bottom > window.innerHeight - 10)
          tooltipEl.style.top = (clientY - rect.height - 10) + 'px';
      });
    };

    const showTooltip = (event, d, isGroup) => {
      if (!tooltipEl || !tooltipsEnabled) return;
      tooltipEl.style.display = 'block';
      tooltipEl.innerHTML = buildTooltipHTML(d, isGroup);
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;
      positionTooltip(clientX, clientY);
    };

    const moveTooltip = (event) => {
      if (!tooltipEl) return;
      tooltipEl.style.left = (event.clientX + 15) + 'px';
      tooltipEl.style.top = (event.clientY - 10) + 'px';
    };

    const hideTooltip = () => {
      if (tooltipEl) tooltipEl.style.display = 'none';
    };

    // Tap elsewhere to dismiss tooltip on touch
    svg.on('click.tooltip', (event) => {
      if (lastEventWasTouch && (event.target === svgEl || event.target.classList.contains('group-bg'))) {
        hideTooltip();
      }
    });

    // ═══ RENDER ═══

    // 1. Group backgrounds
    const internalNodes = root.descendants().filter(d => d.children && d.depth > 0);

    g.selectAll('.group-bg')
      .data(internalNodes)
      .enter()
      .append('rect')
      .attr('class', 'group-bg')
      .attr('x', d => d.x0)
      .attr('y', d => d.y0)
      .attr('width', d => d.x1 - d.x0)
      .attr('height', d => d.y1 - d.y0)
      .attr('fill', '#131722')
      .attr('stroke', d => d.depth === 1 ? '#3a3a3a' : '#2a2a2a')
      .attr('stroke-width', d => d.depth === 1 ? 2.5 : 1.5)
      .style('cursor', 'default')
      .on('mouseover', (event, d) => { if (!lastEventWasTouch) showTooltip(event, d, true); })
      .on('mousemove', (event) => { if (!lastEventWasTouch) moveTooltip(event); })
      .on('mouseout', () => { if (!lastEventWasTouch) hideTooltip(); })
      .on('touchstart', (event, d) => {
        setTimeout(() => { if (!touchMoved && touchCount === 1) showTooltip(event, d, true); }, 150);
      }, { passive: true });

    // 2. Group labels
    if (groupBy !== 'none') {
      const labelNodes = internalNodes.filter(d => {
        const w = d.x1 - d.x0;
        const h = d.y1 - d.y0;
        const minH = d.depth === 1 ? 28 : 40;
        return w > 50 && h > minH;
      });

      g.selectAll('.group-label-bg')
        .data(labelNodes)
        .enter()
        .append('rect')
        .attr('class', 'group-label-bg')
        .attr('x', d => d.x0)
        .attr('y', d => d.y0)
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.depth === 1 ? 26 : 22)
        .attr('fill', d => d.depth === 1 ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,40,0.65)')
        .attr('pointer-events', 'none');

      g.selectAll('.group-label')
        .data(labelNodes)
        .enter()
        .append('text')
        .attr('class', 'group-label')
        .attr('x', d => d.x0 + 8)
        .attr('y', d => d.y0 + (d.depth === 1 ? 13 : 11))
        .attr('dy', '0.35em')
        .attr('fill', '#e0e0e0')
        .attr('font-size', d => d.depth === 1 ? '12px' : '11px')
        .attr('font-weight', 600)
        .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
        .attr('pointer-events', 'none')
        .text(d => {
          const maxChars = Math.floor((d.x1 - d.x0 - 20) / 7);
          const label = d.data.name + ' ›';
          return label.length > maxChars ? label.slice(0, maxChars - 1) + '…' : label;
        });
    }

    // 3. Leaf cells
    const cells = g.selectAll('.cell')
      .data(leaves)
      .enter()
      .append('g')
      .attr('class', 'cell')
      .style('cursor', 'pointer')
      .on('mouseover', (event, d) => { if (!lastEventWasTouch) showTooltip(event, d, false); })
      .on('mousemove', (event) => { if (!lastEventWasTouch) moveTooltip(event); })
      .on('mouseout', () => { if (!lastEventWasTouch) hideTooltip(); })
      .on('touchstart', (event, d) => {
        setTimeout(() => { if (!touchMoved && touchCount === 1) showTooltip(event, d, false); }, 150);
      }, { passive: true });

    // Stock rectangles
    cells.append('rect')
      .attr('x', d => d.x0)
      .attr('y', d => d.y0)
      .attr('width', d => Math.max(0, d.x1 - d.x0))
      .attr('height', d => Math.max(0, d.y1 - d.y0))
      .attr('fill', d => d.data.color)
      .attr('stroke', '#1a1a1a')
      .attr('stroke-width', 0.5);

    const textGroups = cells.append('g')
      .attr('pointer-events', 'none');

    // Ticker labels — hidden by default, shown based on zoom
    textGroups.append('text')
      .attr('class', 'ticker-label')
      .attr('x', d => d.x0 + (d.x1 - d.x0) / 2)
      .attr('y', d => {
        const h = d.y1 - d.y0;
        return d.y0 + (h > 30 ? h / 2 - 6 : h / 2);
      })
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#fff')
      .attr('font-weight', 700)
      .attr('font-size', d => {
        const w = d.x1 - d.x0;
        const h = d.y1 - d.y0;
        return Math.max(6, Math.min(14, Math.min(w / 4, h / 3))) + 'px';
      })
      .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
      .attr('filter', 'url(#text-shadow)')
      .attr('display', 'none')
      .text(d => d.data.ticker);

    // Change % labels — hidden by default
    textGroups.append('text')
      .attr('class', 'change-label')
      .attr('x', d => d.x0 + (d.x1 - d.x0) / 2)
      .attr('y', d => {
        const h = d.y1 - d.y0;
        return d.y0 + h / 2 + 8;
      })
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#fff')
      .attr('font-weight', 600)
      .attr('font-size', d => {
        const w = d.x1 - d.x0;
        const h = d.y1 - d.y0;
        return Math.max(5, Math.min(11, Math.min(w / 5, h / 4))) + 'px';
      })
      .attr('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
      .attr('filter', 'url(#text-shadow)')
      .attr('display', 'none')
      .text(d => {
        const p = d.data.changePercent;
        return (p >= 0 ? '+' : '') + p.toFixed(2) + '%';
      });

    function updateLabelVisibility(zoomScale) {
      g.selectAll('.cell').each(function (d) {
        const cellW = (d.x1 - d.x0) * zoomScale;
        const cellH = (d.y1 - d.y0) * zoomScale;
        const w = d.x1 - d.x0;
        const h = d.y1 - d.y0;
        const cy = d.y0 + h / 2;
        const cell = d3.select(this);

        const showTicker = cellW >= 35 && cellH >= 20;
        const showChange = cellW >= 35 && cellH >= 38;

        if (showTicker) {
          const tickerLen = (d.data.ticker || '').length || 3;
          const maxFontW = cellW / (tickerLen * 0.7);
          const maxFontH = showChange ? cellH / 3.5 : cellH / 2.5;
          let fontSize = Math.min(maxFontW, maxFontH, 16 * zoomScale) / zoomScale;

          const tickerEl = cell.select('.ticker-label')
            .attr('display', null)
            .attr('font-size', fontSize + 'px');

          const actualWidth = tickerEl.node().getComputedTextLength();
          if (actualWidth > w * 0.9) {
            fontSize = fontSize * (w * 0.9) / actualWidth;
            tickerEl.attr('font-size', Math.max(1, fontSize) + 'px');
          }

          const tickerY = showChange ? cy - fontSize * 0.35 : cy;
          tickerEl.attr('y', tickerY);
        } else {
          cell.select('.ticker-label').attr('display', 'none');
        }

        if (showChange) {
          const maxFontW = cellW / (7 * 0.7);
          const maxFontH = cellH / 5;
          const fontSize = Math.min(maxFontW, maxFontH, 12 * zoomScale) / zoomScale;
          const changeY = cy + fontSize * 0.7;

          cell.select('.change-label')
            .attr('display', null)
            .attr('font-size', Math.max(1, fontSize) + 'px')
            .attr('y', changeY);
        } else {
          cell.select('.change-label').attr('display', 'none');
        }
      });

      g.selectAll('.group-label-bg').each(function (d) {
        const w = (d.x1 - d.x0) * zoomScale;
        const h = (d.y1 - d.y0) * zoomScale;
        const minH = d.depth === 1 ? 28 : 40;
        const show = w > 50 && h > minH;
        d3.select(this).attr('display', show ? null : 'none');
      });
      g.selectAll('.group-label').each(function (d) {
        const w = (d.x1 - d.x0) * zoomScale;
        const h = (d.y1 - d.y0) * zoomScale;
        const minH = d.depth === 1 ? 28 : 40;
        const show = w > 50 && h > minH;
        d3.select(this).attr('display', show ? null : 'none');
      });
    }

    updateLabelVisibility(1);

  }, [data, groupBy, sizeBy, colorBy]);

  // ─── Trigger render ───
  useEffect(() => {
    if (!isLoading && data.length) {
      const timer = setTimeout(renderTreemap, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, data, groupBy, sizeBy, colorBy, renderTreemap]);

  // ─── Window resize ───
  useEffect(() => {
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(renderTreemap, 200);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [renderTreemap]);

  // ─── Reset Zoom ───
  const handleResetZoom = useCallback(() => {
    if (zoomRef.current) {
      const { zoom, svg } = zoomRef.current;
      svg.transition().duration(300).call(zoom.transform, d3.zoomIdentity);
    }
  }, []);

  // ─── Download ───
  const handleDownload = useCallback(async (format) => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const clone = svgEl.cloneNode(true);
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', '#131722');
    clone.insertBefore(bg, clone.firstChild);

    const w = svgEl.clientWidth;
    const h = svgEl.clientHeight;
    clone.setAttribute('width', w);
    clone.setAttribute('height', h);

    // Add logo watermark
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.src = 'https://marketrodeo.com/static/media/rodeoLogo.e65ef3b2ea5da4e8c0b4.png';

    await new Promise((resolve) => {
      logoImg.onload = resolve;
      logoImg.onerror = resolve; // continue even if logo fails
    });

    if (logoImg.complete && logoImg.naturalWidth > 0) {
      const logoCanvas = document.createElement('canvas');
      const logoH = 40;
      const logoW = (logoImg.naturalWidth / logoImg.naturalHeight) * logoH;
      logoCanvas.width = logoImg.naturalWidth;
      logoCanvas.height = logoImg.naturalHeight;
      logoCanvas.getContext('2d').drawImage(logoImg, 0, 0);
      const base64 = logoCanvas.toDataURL('image/png');

      const urlText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      urlText.setAttribute('x', w - 10);
      urlText.setAttribute('y', h - 8);
      urlText.setAttribute('text-anchor', 'end');
      urlText.setAttribute('fill', 'rgba(255, 255, 255, 1)');
      urlText.setAttribute('font-size', '12px');
      urlText.setAttribute('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif");
      urlText.setAttribute('font-weight', '500');
      urlText.textContent = 'marketrodeo.com';
      clone.appendChild(urlText);

      const svgImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      svgImage.setAttribute('href', base64);
      svgImage.setAttribute('x', w - logoW - 10);
      svgImage.setAttribute('y', h - logoH - 24);
      svgImage.setAttribute('width', logoW);
      svgImage.setAttribute('height', logoH);
      svgImage.setAttribute('opacity', '1');
      clone.appendChild(svgImage);
    }

    const svgString = new XMLSerializer().serializeToString(clone);

    if (format === 'SVG') {
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      downloadBlob(blob, `${selectedIndex}-heatmap-${colorBy}.svg`);
      setAnchorDownload(null);
      return;
    }

    const canvas = document.createElement('canvas');
    const scale = 2;
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext('2d');

    const img = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      ctx.drawImage(img, 0, 0, w * scale, h * scale);
      URL.revokeObjectURL(url);
      canvas.toBlob((canvasBlob) => {
        if (canvasBlob) downloadBlob(canvasBlob, `${selectedIndex}-heatmap-${colorBy}.${format.toLowerCase()}`);
      }, format === 'PNG' ? 'image/png' : 'image/jpeg', 0.95);
    };
    img.src = url;
    setAnchorDownload(null);
  }, [selectedIndex, colorBy]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getSizeLabel = () => SIZE_OPTIONS.find(o => o.value === sizeBy)?.label || 'Market cap';
  const getColorLabel = () => COLOR_OPTIONS.find(o => o.value === colorBy)?.label || 'Change D, %';
  const getGroupLabel = () => GROUP_OPTIONS.find(o => o.value === groupBy)?.label || 'Sector';

  // ═══ JSX ═══
  return (
    <Box sx={{ p: 2, bgcolor: '#131722', minHeight: '100vh' }}>
      <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', mb: 2 }}>
        Stock Heatmap
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        mb={2}
        flexWrap="wrap"
        alignItems="center"
        justifyContent={isMobile ? "center" : "flex-start"}
        sx={{
          '& .MuiButton-root': {
            bgcolor: '#2a2e39',
            color: '#d1d4dc',
            borderColor: '#363a45',
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            px: isMobile ? 1 : 1.5,
            py: 0.5,
            minWidth: isMobile ? 40 : 'auto',
            justifyContent: 'center',
            '&:hover': { bgcolor: '#363a45', borderColor: '#4a4e59' }
          }
        }}
      >
        {/* INDEX SELECTOR */}
        <div>
          <Button
            variant="contained"
            disableElevation
            onClick={(e) => setAnchorIndex(e.currentTarget)}
            sx={{
              bgcolor: '#2962ff !important',
              color: '#fff !important',
              '&:hover': { bgcolor: '#1e53e4 !important' },
              minWidth: isMobile ? 44 : 'auto'
            }}
          >
            {isMobile ? '📊' : <>📊 {INDEX_OPTIONS.find(o => o.value === selectedIndex)?.label || selectedIndex}</>}
            <ArrowDropDownIcon sx={{ ml: 0.5, fontSize: 18 }} />
          </Button>
          <Menu
            anchorEl={anchorIndex}
            open={Boolean(anchorIndex)}
            onClose={() => setAnchorIndex(null)}
            PaperProps={{ sx: { bgcolor: '#1e222d', color: '#d1d4dc', minWidth: 180 } }}
          >
            <Typography sx={{ px: 2, py: 1, fontSize: '11px', color: '#787b86', fontWeight: 600 }}>
              INDEX
            </Typography>
            {INDEX_OPTIONS.map(o => (
              <MenuItem
                key={o.value}
                onClick={() => { setSelectedIndex(o.value); setAnchorIndex(null); }}
                selected={selectedIndex === o.value}
                sx={{
                  fontSize: '13px',
                  '&.Mui-selected': { bgcolor: 'rgba(41,98,255,0.2)' },
                  '&:hover': { bgcolor: '#2a2e39' }
                }}
              >
                {o.label}
              </MenuItem>
            ))}
          </Menu>
        </div>

        {/* SIZE */}
        <div>
          <Button
            variant="outlined"
            onClick={(e) => setAnchorSize(e.currentTarget)}
            disabled={isLoading}
            sx={{ minWidth: isMobile ? 44 : 'auto' }}
          >
            {isMobile ? '📐' : <>📐 {getSizeLabel()}</>}
          </Button>
          <Menu
            anchorEl={anchorSize}
            open={Boolean(anchorSize)}
            onClose={() => setAnchorSize(null)}
            PaperProps={{ sx: { bgcolor: '#1e222d', color: '#d1d4dc', minWidth: 180 } }}
          >
            <Typography sx={{ px: 2, py: 1, fontSize: '11px', color: '#787b86', fontWeight: 600 }}>
              SIZE BY
            </Typography>
            {SIZE_OPTIONS.map(o => (
              <MenuItem
                key={o.value}
                onClick={() => { setSizeBy(o.value); setAnchorSize(null); }}
                selected={sizeBy === o.value}
                sx={{
                  fontSize: '13px',
                  '&.Mui-selected': { bgcolor: 'rgba(41,98,255,0.2)' },
                  '&:hover': { bgcolor: '#2a2e39' }
                }}
              >
                {o.label}
              </MenuItem>
            ))}
          </Menu>
        </div>

        {/* COLOR */}
        <div>
          <Button
            variant="outlined"
            onClick={(e) => setAnchorColor(e.currentTarget)}
            disabled={isLoading}
            sx={{ minWidth: isMobile ? 44 : 'auto' }}
          >
            {isMobile ? '🎨' : <>🎨 {getColorLabel()}</>}
          </Button>
          <Menu
            anchorEl={anchorColor}
            open={Boolean(anchorColor)}
            onClose={() => setAnchorColor(null)}
            PaperProps={{ sx: { bgcolor: '#1e222d', color: '#d1d4dc', minWidth: 200 } }}
          >
            <Typography sx={{ px: 2, py: 1, fontSize: '11px', color: '#787b86', fontWeight: 600 }}>
              COLOR BY
            </Typography>
            {COLOR_OPTIONS.map(o => (
              <MenuItem
                key={o.value}
                onClick={() => { setColorBy(o.value); setAnchorColor(null); }}
                selected={colorBy === o.value}
                sx={{
                  fontSize: '13px',
                  '&.Mui-selected': { bgcolor: 'rgba(41,98,255,0.2)' },
                  '&:hover': { bgcolor: '#2a2e39' }
                }}
              >
                {o.label}
              </MenuItem>
            ))}
          </Menu>
        </div>

        {/* GROUP */}
        <div>
          <Button
            variant="outlined"
            onClick={(e) => setAnchorGroup(e.currentTarget)}
            disabled={isLoading}
            sx={{ minWidth: isMobile ? 44 : 'auto' }}
          >
            {isMobile ? '⚙️' : <>⚙️ {getGroupLabel()}</>}
          </Button>
          <Menu
            anchorEl={anchorGroup}
            open={Boolean(anchorGroup)}
            onClose={() => setAnchorGroup(null)}
            PaperProps={{ sx: { bgcolor: '#1e222d', color: '#d1d4dc', minWidth: 180 } }}
          >
            <Typography sx={{ px: 2, py: 1, fontSize: '11px', color: '#787b86', fontWeight: 600 }}>
              GROUP BY
            </Typography>
            {GROUP_OPTIONS.map(o => (
              <MenuItem
                key={o.value}
                onClick={() => { setGroupBy(o.value); setAnchorGroup(null); }}
                selected={groupBy === o.value}
                sx={{
                  fontSize: '13px',
                  '&.Mui-selected': { bgcolor: 'rgba(41,98,255,0.2)' },
                  '&:hover': { bgcolor: '#2a2e39' }
                }}
              >
                {o.label}
              </MenuItem>
            ))}
          </Menu>
        </div>

        {/* RESET ZOOM */}
        <Tooltip title="Reset zoom">
          <IconButton
            onClick={handleResetZoom}
            disabled={isLoading}
            sx={{
              color: '#d1d4dc',
              bgcolor: '#2a2e39',
              border: '1px solid #363a45',
              borderRadius: 1,
              '&:hover': { bgcolor: '#363a45' }
            }}
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* DOWNLOAD */}
        <div>
          <Button
            variant="outlined"
            onClick={(e) => setAnchorDownload(e.currentTarget)}
            disabled={isLoading || !data.length}
            sx={{ minWidth: isMobile ? 44 : 'auto' }}
          >
            {isMobile ? <DownloadIcon fontSize="small" /> : <> <DownloadIcon fontSize="small" sx={{ mr: 1 }} /> Download </>}
          </Button>
          <Menu
            anchorEl={anchorDownload}
            open={Boolean(anchorDownload)}
            onClose={() => setAnchorDownload(null)}
            PaperProps={{ sx: { bgcolor: '#1e222d', color: '#d1d4dc' } }}
          >
            {DOWNLOAD_FORMATS.map(fmt => (
              <MenuItem
                key={fmt}
                onClick={() => handleDownload(fmt)}
                sx={{ fontSize: '13px', '&:hover': { bgcolor: '#2a2e39' } }}
              >
                {fmt}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </Stack>

      <Typography variant="caption" sx={{ color: '#787b86', mb: 1, display: 'block' }}>
        💡 Scroll to zoom • Drag to pan when zoomed in • Reset button returns to full view
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box
        ref={containerRef}
        sx={{
          height: 'calc(100vh - 200px)',
          minHeight: '600px',
          bgcolor: '#131722',
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          border: '1px solid #2a2e39'
        }}
      >
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <CircularProgress sx={{ color: '#2962ff' }} />
            <Typography sx={{ mt: 2, color: '#787b86' }}>Loading heatmap data...</Typography>
          </Stack>
        ) : data.length ? (
          <svg ref={svgRef} style={{ width: '100%', height: '100%', display: 'block', overflow: 'hidden', touchAction: 'none' }} />
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <Typography variant="h6" sx={{ color: '#787b86' }}>No data available for {selectedIndex}</Typography>
          </Stack>
        )}

        <div
          ref={tooltipRef}
          style={{
            position: 'fixed', display: 'none', pointerEvents: 'none', zIndex: 9999,
            padding: '12px', backgroundColor: 'rgba(20, 22, 30, 0.95)',
            border: '1px solid #444', borderRadius: '4px', color: '#fff',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontSize: '12px', maxWidth: '280px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
          }}
        />
      </Box>

      {data.length > 0 && (
        <Typography variant="body2" sx={{ mt: 1, color: '#787b86', fontSize: '12px' }}>
          Showing {data.length} companies
          {groupBy === 'sector' && ` across ${new Set(data.map(d => d.sector)).size} sectors`}
          {groupBy === 'sector-industry' && ` across ${new Set(data.map(d => d.sector)).size} sectors and ${new Set(data.map(d => d.industry)).size} industries`}
        </Typography>
      )}
    </Box>
  );
}